import { p as onFirstInteraction, H as getBaseFontSize, G as isKeyboardUser } from "./utilities-Da0sjanJ.js";
class HeaderV3Component extends HTMLElement {
  connectedCallback() {
    this._init();
  }
  _init() {
    this.cfg = {
      sticky: this.dataset.sticky === "true",
      stickyMode: this.dataset.stickyMode || "always",
      colorScheme: this.dataset.colorScheme || "color-scheme-1",
      stickyColorScheme: this.dataset.stickyColorScheme || "color-scheme-1",
      position: this.dataset.position || "outside",
      transparent: this.dataset.transparent || "fill",
      measureHeight: this.dataset.measureHeight !== "false"
    };
    this._toggle = this.querySelector(".header-toggle");
    this._nav = this.querySelector(".header-drawer");
    this._menus = this.querySelectorAll("menu-header-v2");
    this._isSticky = false;
    this._isHidden = false;
    this._isNavOpen = false;
    this._isHovered = false;
    this._scrollY = 0;
    this._isMobile = void 0;
    this._unsubs = [];
    this._interactiveReady = false;
    this._deferInteractive();
    let attempts = 0;
    const doInit = () => {
      const header = this.querySelector(".header-v3");
      if (!header) {
        if (++attempts > 60) {
          return;
        }
        requestAnimationFrame(doInit);
        return;
      }
      window.matchMedia("(max-width: 991px)").matches;
      let isMbWidth = window.matchMedia("(max-width: 991px)").matches;
      let initialScrollY = window.scrollY;
      this.classList.add(this.cfg.colorScheme);
      this._currentScheme = this.cfg.colorScheme;
      this._setupLogoColors();
      if (header && this.cfg.measureHeight) {
        this._lastHeaderH = null;
        this._headerRo = new ResizeObserver((entries) => {
          const entry = entries[0];
          let h;
          if (entry.borderBoxSize && entry.borderBoxSize[0]) {
            h = Math.round(entry.borderBoxSize[0].blockSize);
          } else {
            h = Math.round(header.getBoundingClientRect().height);
          }
          if (this._lastHeaderH === h) return;
          this._lastHeaderH = h;
          const isMb = window.matchMedia("(max-width: 991px)").matches;
          if (isMb) {
            document.documentElement.style.setProperty("--actual-header-height-mb", `${h}px`);
          } else {
            document.documentElement.style.setProperty("--actual-header-height", `${h}px`);
          }
          if (this.cfg.sticky) this._updateSidebarTop();
        });
        this._headerRo.observe(header);
        this._unsubs.push(() => this._headerRo.disconnect());
      }
      if (this.cfg.sticky) {
        let stickyInitDone = false;
        const lazyInitSticky = () => {
          if (stickyInitDone) return;
          stickyInitDone = true;
          const bg = this.querySelector(".header-v3-bg");
          const bgSticky = this.querySelector(".header-v3-bg-sticky");
          if (bg && bgSticky) {
            let bgW = 0, stickyW = 0;
            this._bgStickyRo = new ResizeObserver((entries) => {
              var _a;
              for (const entry of entries) {
                const w = entry.borderBoxSize ? entry.borderBoxSize[0].inlineSize : entry.contentRect.width;
                if (entry.target === bg) bgW = w;
                if (entry.target === bgSticky) stickyW = w;
              }
              if (bgW > 0 && stickyW > 0) {
                this.style.setProperty("--_scale-x", (bgW / stickyW).toFixed(4));
                (_a = this._bgStickyRo) == null ? void 0 : _a.disconnect();
              }
            });
            this._bgStickyRo.observe(bg);
            this._bgStickyRo.observe(bgSticky);
            this._unsubs.push(() => {
              var _a;
              return (_a = this._bgStickyRo) == null ? void 0 : _a.disconnect();
            });
          }
          this._initStickyConfig(16, isMbWidth, initialScrollY);
        };
        if (initialScrollY > 0) {
          lazyInitSticky();
        } else {
          const onInteract = () => {
            ["scroll", "wheel", "touchmove"].forEach((evt) => window.removeEventListener(evt, onInteract));
            lazyInitSticky();
          };
          ["scroll", "wheel", "touchmove"].forEach((evt) => window.addEventListener(evt, onInteract, { passive: true }));
          this._updateSidebarTop();
        }
      }
      this.classList.add("is-ready");
    };
    requestAnimationFrame(doInit);
  }
  /**
   * JIT: Defer interactive event wiring behind first user interaction.
   * Uses shared onFirstInteraction gate (includes bot detection —
   * Lighthouse sees the fully-initialized header).
   */
  _deferInteractive() {
    onFirstInteraction(() => {
      if (this._interactiveReady) return;
      this._interactiveReady = true;
      this._setupMenuEvents();
      this._setupMobileNav();
      this._setupHoverScheme();
      let isMb = window.matchMedia("(max-width: 991px)").matches;
      let mgTop = parseFloat(this.dataset[isMb ? "headerMgTopMb" : "headerMgTop"] || "0");
      if (mgTop > 0) {
        requestAnimationFrame(() => {
          this._stickyThreshold = mgTop * getBaseFontSize();
        });
      }
      this._resizeRAF = null;
      this._onResize = () => {
        if (this._resizeRAF) return;
        this._resizeRAF = requestAnimationFrame(() => {
          this._handleResize();
          this._resizeRAF = null;
        });
      };
      window.addEventListener("resize", this._onResize, { passive: true });
    });
  }
  _setupLogoColors() {
    const items = (cls) => this.querySelectorAll(`.logo-header-v2-content-item.${cls}`);
    items("item-inverse").forEach((el) => el.classList.add(this.cfg.colorScheme));
    items("item-normal").forEach((el) => el.classList.add(this.cfg.stickyColorScheme));
  }
  // ─── Sticky ────────────────────────────────────────────────────────────────
  _initStickyConfig(rootFz, isMb, initialScrollY) {
    this._stickyThreshold = parseFloat(this.dataset[isMb ? "headerMgTopMb" : "headerMgTop"] || "0") * rootFz;
    const HIDE = isMb ? 50 : 100;
    let lastY = initialScrollY;
    if (this.cfg.stickyMode === "scroll-up") {
      // ZAND&ZOUT 17 okt 2026: transparante header bovenaan niet meer eerst wit laten
      // worden (witte balk tussen 10 en 50px scrollen). Hij schuift nu transparant weg
      // en wordt pas wit buiten beeld, zodat hij wit terugkomt bij omhoog scrollen.
      const zzTop = this.cfg.position === "inside" && this.cfg.transparent === "transparent";
      this._handleScrollData = (d) => {
        if (this._isNavOpen) return;
        const y = d.scrollY;
        const delta = y - lastY;
        lastY = y;
        let wantSticky = y > (zzTop ? Math.max(this._stickyThreshold, HIDE) : this._stickyThreshold);
        const locked = this._hasActiveDropdown();
        let wantHidden = this._isHidden;
        if (y <= HIDE || locked) wantHidden = false;
        else if (delta > 0) wantHidden = true;
        else if (delta < 0) wantHidden = false;
        if (zzTop && wantSticky && wantHidden && !this._isSticky) {
          wantSticky = false;
          if (!this._zzStickyTimer) {
            this._zzStickyTimer = setTimeout(() => {
              this._zzStickyTimer = null;
              if (this._isHidden && !this._isSticky) this._setSticky(true);
            }, 600);
          }
        }
        if (wantSticky !== this._isSticky || wantHidden !== this._isHidden) {
          this._setSticky(wantSticky);
          this._setHidden(wantHidden);
        }
      };
    } else {
      this._handleScrollData = (d) => {
        if (this._isNavOpen) return;
        const wantSticky = d.scrollY > this._stickyThreshold;
        if (wantSticky !== this._isSticky) {
          this._setSticky(wantSticky);
        }
      };
    }
    this._handleScrollData({ scrollY: window.scrollY });
    this._updateSidebarTop();
    this._bindToDispatcher();
  }
  _bindToDispatcher() {
    var _a;
    if ((_a = window.Theme) == null ? void 0 : _a.onScrollUpdate) {
      this._unsubs.push(window.Theme.onScrollUpdate(this._handleScrollData));
    } else {
      const handler = (e) => {
        window.removeEventListener("scroll-dispatcher:ready", handler);
        this._unsubs.push(
          e.detail.dispatcher.on("update", this._handleScrollData)
        );
      };
      window.addEventListener("scroll-dispatcher:ready", handler);
      this._unsubs.push(() => window.removeEventListener("scroll-dispatcher:ready", handler));
    }
  }
  _setSticky(on) {
    if (this._isSticky === on) return;
    this._isSticky = on;
    this._scheduleDomUpdate();
  }
  _setHidden(on) {
    if (this._isHidden === on) return;
    this._isHidden = on;
    this._scheduleDomUpdate();
    if (on) this._closeMenus();
  }
  _scheduleDomUpdate() {
    if (this._domUpdateRAF) return;
    this._domUpdateRAF = requestAnimationFrame(() => {
      this._domUpdateRAF = null;
      this.classList.toggle("is-sticky", this._isSticky);
      this.classList.toggle("is-hidden", this._isHidden);
      this._updateSidebarTop();
      this._updateColorScheme();
    });
  }
  /**
   * Push --sidebar-top on <body> so the sidebar can position itself
   * without :has() selectors (which pierce containment).
   *
   * When measureHeight is enabled the value references the actual measured
   * header height (--actual-header-height / --actual-header-height-mb),
   * otherwise it falls back to the static --header-minheight calculation.
   *
   * Rules (content-gap is always added as sidebar padding-top):
   *  - No [data-sticky-top]   → remove property (consumers fall back to default)
   *  - Not sticky             → remove property (same as no-header)
   *  - Sticky + always        → actual header height (or min-height fallback)
   *  - Sticky + scroll-up     → actual header height (or min-height fallback)
   *  - Sticky + scroll-up + hidden → remove property (same as no-header)
   */
  _updateSidebarTop() {
    onFirstInteraction(() => {
      if (this.cfg.stickyMode === "scroll-up" && this._isHidden) {
        document.body.style.removeProperty("--sidebar-top");
        this._lastSidebarTop = null;
        return;
      }
      const isMb = window.matchMedia("(max-width: 991px)").matches;
      let hVar;
      if (isMb) {
        hVar = "var(--header-minheight-mb, 3.25rem)";
      } else {
        hVar = this.cfg.measureHeight ? "var(--actual-header-height, var(--header-minheight, 3.75rem))" : "var(--header-minheight, 3.75rem)";
      }
      const newVal = `calc(${hVar} + var(--section-gap, 0px))`;
      if (this._lastSidebarTop === newVal) return;
      this._lastSidebarTop = newVal;
      document.body.style.setProperty("--sidebar-top", newVal);
    }, 0);
  }
  // ─── Color Scheme ──────────────────────────────────────────────────────────
  _updateColorScheme() {
    if (this.cfg.position !== "inside" || this.cfg.transparent !== "transparent") return;
    const bgVisible = this._isSticky || this._hasActiveDropdown() || this._isNavOpen || this._isHovered;
    const target = bgVisible ? this.cfg.stickyColorScheme : this.cfg.colorScheme;
    if (this._currentScheme === target) return;
    const prev = this._currentScheme;
    this._currentScheme = target;
    if (prev) this.classList.replace(prev, target);
    else this.classList.add(target);
  }
  /** Track header hover for display-bg-when-hover so color scheme follows the bg. */
  _setupHoverScheme() {
    if (this.dataset.displayBgWhenHover !== "true") return;
    this.addEventListener("mouseenter", () => {
      this._isHovered = true;
      this._updateColorScheme();
    });
    this.addEventListener("mouseleave", () => {
      this._isHovered = false;
      this._updateColorScheme();
    });
  }
  // ─── Menu Events ───────────────────────────────────────────────────────────
  /** Check actual DOM for any active dropdown or megamenu (no counter needed). */
  _hasActiveDropdown() {
    return !!(this.querySelector(".menu-header-v3-item.is-dropdown-active") || this.querySelector(".menu-header-v3-item.is-megamenu-active") || this.querySelector(".megamenu-v2.active") || this.querySelector("dropdown-localization-component.i18n-active"));
  }
  _setupMenuEvents() {
    this.addEventListener("dropdown:open", () => {
      clearTimeout(this._menuCloseTimer);
      this.classList.add("is-menu-open");
      this._updateColorScheme();
    });
    this.addEventListener("dropdown:close", () => {
      clearTimeout(this._menuCloseTimer);
      this._menuCloseTimer = setTimeout(() => {
        if (!this._hasActiveDropdown()) {
          this.classList.remove("is-menu-open", "is-megamenu-active");
        }
        this._updateColorScheme();
      }, 150);
    });
    document.addEventListener("shopify:block:select", (e) => {
      const mega = this.querySelector(`.megamenu-v2[id="${e.detail.blockId}"]`);
      if (mega) {
        mega.classList.add("active");
        this.classList.add("is-menu-open", "is-megamenu-active");
        this._updateColorScheme();
      }
    });
    document.addEventListener("shopify:block:deselect", (e) => {
      const mega = this.querySelector(`.megamenu-v2[id="${e.detail.blockId}"]`);
      if (mega) {
        mega.classList.remove("active");
        this.classList.remove("is-menu-open", "is-megamenu-active");
        this._updateColorScheme();
      }
    });
  }
  _closeMenus() {
    this._menus.forEach((m) => m.dispatchEvent(new CustomEvent("close-all-dropdowns")));
    this.classList.remove("is-menu-open", "is-megamenu-active");
  }
  // ─── Mobile Nav ────────────────────────────────────────────────────────────
  _setupMobileNav() {
    if (!this._toggle || !this._nav) return;
    this._toggle.addEventListener("click", () => this._toggleNav());
  }
  _toggleNav() {
    var _a;
    this._isNavOpen = !this._isNavOpen;
    const open = this._isNavOpen;
    let navTop = 0;
    let margin = 0;
    let cachedScrollY = 0;
    if (open) {
      navTop = this._nav.getBoundingClientRect().top;
      margin = parseFloat(getComputedStyle(this._nav).marginBottom) || 0;
      if (!((_a = window.Theme) == null ? void 0 : _a.lenis)) {
        cachedScrollY = window.pageYOffset;
      }
    }
    const isMobile = window.matchMedia("(max-width: 991px)").matches;
    this._nav.classList.toggle("active", open);
    this._toggle.classList.toggle("active", open);
    this.classList.toggle("is-open", open);
    if (open) {
      this.dispatchEvent(new CustomEvent("header:drawer-open"));
    }
    this._toggle.setAttribute("aria-expanded", String(open));
    this._toggle.setAttribute("aria-label", open ? "Close navigation menu" : "Open navigation menu");
    if (isMobile) {
      this._nav.setAttribute("aria-hidden", String(!open));
      this._nav.inert = !open;
      if (open && isKeyboardUser()) {
        const first = this._nav.querySelector('a, button, [tabindex="0"]');
        if (first) first.focus();
      }
    } else {
      this._nav.removeAttribute("aria-hidden");
      this._nav.inert = false;
    }
    if (open) {
      this.style.setProperty("--nav-top-offset", `${navTop + margin}px`);
      this._lockScroll(cachedScrollY);
    } else {
      this._unlockScroll();
      this._menus.forEach((m) => {
        var _a2;
        return (_a2 = m._resetMobileNavigation) == null ? void 0 : _a2.call(m);
      });
    }
    this._updateColorScheme();
  }
  /** Close the mobile nav programmatically (e.g. from external components). */
  closeMobileNav() {
    if (this._isNavOpen) this._toggleNav();
  }
  _lockScroll(cachedScrollY) {
    var _a;
    if ((_a = window.Theme) == null ? void 0 : _a.lenis) {
      window.Theme.lenis.stop();
    } else {
      this._scrollY = cachedScrollY !== void 0 ? cachedScrollY : window.pageYOffset;
      Object.assign(document.body.style, {
        overflow: "hidden",
        position: "fixed",
        top: `-${this._scrollY}px`,
        width: "100%"
      });
    }
  }
  _unlockScroll() {
    var _a;
    if ((_a = window.Theme) == null ? void 0 : _a.lenis) {
      window.Theme.lenis.start();
    } else {
      Object.assign(document.body.style, { overflow: "", position: "", top: "", width: "" });
      window.scrollTo(0, this._scrollY);
    }
  }
  // ─── Resize ────────────────────────────────────────────────────────────────
  _handleResize() {
    var _a, _b;
    const isMobile = window.matchMedia("(max-width: 991px)").matches;
    const wasInitialized = this._isMobile !== void 0;
    if (this._isMobile === isMobile) return;
    this._isMobile = isMobile;
    if (!wasInitialized) return;
    if (this._isNavOpen) {
      (_a = this._nav) == null ? void 0 : _a.classList.remove("active");
      (_b = this._toggle) == null ? void 0 : _b.classList.remove("active");
      this.classList.remove("is-open");
      this._isNavOpen = false;
      this._unlockScroll();
      this._menus.forEach((m) => {
        var _a2;
        return (_a2 = m._resetMobileNavigation) == null ? void 0 : _a2.call(m);
      });
    }
    this._closeMenus();
    this._updateColorScheme();
    if (this.cfg.sticky) this._updateSidebarTop();
  }
  /** Measure the rendered header height and write only the current breakpoint's var.
   *  Called on breakpoint cross so each viewport gets its correct value. */
  _measureHeaderHeight() {
    if (!this.cfg.measureHeight) return;
    const header = this.querySelector(".header-v3");
    if (!header) return;
    let h;
    if (this._lastHeaderH != null) {
      h = this._lastHeaderH;
    } else {
      return;
    }
    const isMb = window.matchMedia("(max-width: 991px)").matches;
    if (isMb) {
      document.documentElement.style.setProperty("--actual-header-height-mb", `${h}px`);
    } else {
      document.documentElement.style.setProperty("--actual-header-height", `${h}px`);
    }
  }
  // ─── Cleanup ───────────────────────────────────────────────────────────────
  disconnectedCallback() {
    this._unsubs.forEach((fn) => fn());
    window.removeEventListener("resize", this._onResize);
    if (this._resizeRAF) cancelAnimationFrame(this._resizeRAF);
    if (this._isNavOpen) this._unlockScroll();
  }
}
if (!customElements.get("header-v3-component")) {
  customElements.define("header-v3-component", HeaderV3Component);
}
